/**
 * companyNavConfig — domain definitions for the /company/* rail.
 *
 * Mirrors the shape of the location-side `navConfig.ts` (Domain[] →
 * RailSidebar) so both scopes share the same rendering primitive
 * (RailSidebar with `variant: 'company'` for the visual diff). Lifts
 * the previously hand-rolled CompanySidebar groups into the same
 * Operation / People / Finance / Admin shape, and applies the
 * audit-driven cleanup landed alongside this file:
 *
 *   - Drop /company/dashboard (overlapped /company/overview).
 *   - Drop /company/locations/overview (per-location detail page
 *     already covers it).
 *   - Drop standalone /company/banks rail entry (kept as a tab inside
 *     /company/treasury).
 *   - Promote Tokens into People (it's a per-staff config).
 *   - Promote Locations into Operation (it's the everyday cross-tier
 *     navigation, not an admin chore).
 */

import { type ComponentType } from 'react';
import {
  Briefcase,
  Coins,
  CreditCard,
  FileText,
  HandCoins,
  LayoutDashboard,
  Mail,
  Plug,
  Users,
  Wallet,
  Building2,
  ShieldCheck,
} from 'lucide-react';

import type { TFunction } from 'i18next';

import type { Domain, PanelItem } from './navConfig';

/**
 * Permission gates that the company rail uses to hide entire entries.
 * Mirrors the shape of `GateFlags` on the location side; only the
 * fields that matter at company tier are surfaced.
 */
export interface CompanyGateFlags {
  canSeeCompanyUsers: boolean;
  canSeeCompanyLocations: boolean;
  canSeeCompanyIntegrations: boolean;
  canSeeStaff: boolean;
  canSeePayroll: boolean;
  canSeeTreasury: boolean;
  canSeePlatformInvoices: boolean;
}

interface BuildCompanyDomainsParams {
  t: TFunction;
  gates: CompanyGateFlags;
}

/** Drives the rail-button monogram + visible label per domain. */
const COMPANY_DOMAIN_META: Record<
  'operation' | 'people' | 'finance' | 'admin',
  { icon: ComponentType<{ className?: string }>; monogram: string }
> = {
  operation: { icon: LayoutDashboard, monogram: 'OP' },
  people:    { icon: Users,            monogram: 'PE' },
  finance:   { icon: Wallet,           monogram: 'FI' },
  admin:     { icon: ShieldCheck,      monogram: 'AD' },
};

export function buildCompanyDomains({ t, gates }: BuildCompanyDomainsParams): Domain[] {
  // ── Operation ─────────────────────────────────────────────────────
  // Cross-tier "where am I working today" surfaces. Overview is the
  // canonical landing page; Locations is the everyday list.
  const operation: PanelItem[] = [
    {
      title: t('companyNav.overview', 'Overview'),
      icon: LayoutDashboard,
      url: '/company/overview',
    },
    {
      title: t('companyNav.locations', 'Locations'),
      icon: Building2,
      url: '/company/locations',
      gate: gates.canSeeCompanyLocations,
    },
  ];

  // ── People ────────────────────────────────────────────────────────
  // Anything keyed by a person at company tier — users (RBAC), staff
  // roster + positions, payroll, and the cross-location tokens rollup.
  const people: PanelItem[] = [
    // One entry, not two. Employees and Users were two rail entries onto two
    // lists of the same people; People is that single list, and Positions is
    // the only genuinely separate surface underneath it. Roles moved to Admin
    // — they are an access-control object, not a person.
    {
      title: t('companyNav.people', 'People'),
      icon: Users,
      url: '/company/people',
      gate: gates.canSeeStaff || gates.canSeeCompanyUsers,
      matchPrefixes: ['/company/persons'],
      subItems: [
        {
          title: t('companyNav.peopleAll', 'People'),
          url: '/company/people',
          icon: Users,
        },
        {
          title: t('companyNav.staffPositions', 'Positions'),
          url: '/company/staff/positions',
          icon: Briefcase,
        },
      ],
    },
    {
      title: t('companyNav.payroll', 'Payroll'),
      icon: HandCoins,
      url: '/company/payroll',
      gate: gates.canSeePayroll,
    },
    {
      title: t('companyNav.tokens', 'Staff tokens'),
      icon: Coins,
      url: '/company/tokens',
      gate: gates.canSeeStaff,
    },
  ];

  // ── Finance ───────────────────────────────────────────────────────
  // Treasury rolls up bank accounts + Stripe + reconciliation; the
  // standalone /company/banks rail entry is retired (still reachable
  // as a tab inside Treasury).
  const finance: PanelItem[] = [
    {
      title: t('companyNav.treasury', 'Treasury'),
      icon: Wallet,
      url: '/company/treasury',
      gate: gates.canSeeTreasury,
    },
    {
      // What Shaka bills the company — deliberately its own entry rather
      // than a Treasury tab, so it is not read as the business's own money.
      title: t('platformInvoices.title', 'Platform invoices'),
      icon: FileText,
      url: '/company/invoices',
      gate: gates.canSeePlatformInvoices,
    },
  ];

  // ── Admin ─────────────────────────────────────────────────────────
  // Settings + integrations. Mirrors the location-side Settings domain
  // so the icon at the bottom of the rail reads consistently. Settings
  // is the parent surface; the bare `/company/settings` URL redirects to
  // General in App.tsx so old deep-links keep working. Sub-items list
  // every company-tier settings section that has a dedicated route — a
  // smaller subset than location settings since most things (capacity,
  // hours, monetary defaults, staff defaults) are location-scoped.
  //
  // NOTE: there is no in-page tab strip on CompanySettingsPage anymore —
  // the rail's submenu is the single source of navigation. Keep this
  // list in sync with the routes registered in App.tsx and the
  // ``CompanySettingsSection`` union in CompanySettingsPage.tsx.
  const admin: PanelItem[] = [
    // Roles only. The users half of this surface is People; `/company/users`
    // without `?tab=roles` forwards there, so pointing the rail at the bare
    // path would send the operator straight back out of Admin.
    {
      title: t('companyNav.roles', 'Roles'),
      icon: ShieldCheck,
      url: '/company/users?tab=roles',
      matchPrefixes: ['/company/users'],
      gate: gates.canSeeCompanyUsers,
    },
    {
      title: t('companyNav.settingsGeneral', 'General'),
      icon: Building2,
      url: '/company/settings/general',
    },
    {
      title: t('companyNav.settingsBilling', 'Billing'),
      icon: CreditCard,
      url: '/company/settings/billing',
    },
    {
      title: t('companyNav.settingsTokens', 'Staff tokens'),
      icon: Coins,
      url: '/company/settings/tokens',
    },
    {
      title: t('companyNav.settingsEmails', 'Email diagnostics'),
      icon: Mail,
      url: '/company/settings/emails',
    },
    {
      title: t('companyNav.integrations', 'Integrations'),
      icon: Plug,
      url: '/company/settings/integrations',
      gate: gates.canSeeCompanyIntegrations,
    },
  ];

  const pruneItems = (items: PanelItem[]): PanelItem[] =>
    items.filter((item) => item.kind === 'section' || item.gate !== false);

  const operationItems = pruneItems(operation);
  const peopleItems = pruneItems(people);
  const financeItems = pruneItems(finance);
  const adminItems = pruneItems(admin);

  return [
    {
      id: 'admin',
      label: t('companyNav.domains.admin', 'Admin'),
      ...COMPANY_DOMAIN_META.admin,
      hubUrl: '/company/users?tab=roles',
      matchPrefixes: ['/company/settings', '/company/users'],
      items: adminItems,
      visible: adminItems.length > 0,
      separated: true,
    },
    {
      id: 'operation',
      label: t('companyNav.domains.operation', 'Operation'),
      ...COMPANY_DOMAIN_META.operation,
      hubUrl: '/company/overview',
      matchPrefixes: ['/company/overview', '/company/locations'],
      items: operationItems,
      visible: operationItems.length > 0,
    },
    {
      id: 'people',
      label: t('companyNav.domains.people', 'People'),
      ...COMPANY_DOMAIN_META.people,
      hubUrl: '/company/people',
      matchPrefixes: [
        '/company/people',
        '/company/persons',
        '/company/staff',
        '/company/payroll',
        '/company/tokens',
      ],
      items: peopleItems,
      visible: peopleItems.length > 0,
    },
    {
      id: 'finance',
      label: t('companyNav.domains.finance', 'Finance'),
      ...COMPANY_DOMAIN_META.finance,
      hubUrl: '/company/treasury',
      matchPrefixes: ['/company/treasury', '/company/banks'],
      items: financeItems,
      visible: financeItems.length > 0,
    },
  ];
}

export type CompanyDomainsBuilder = typeof buildCompanyDomains;
